import { useCallback, useEffect, useRef } from 'react';

import {
  useDebugStore,
  ALL_CATEGORIES,
  LEVEL_RANK,
  type DebugCategory,
  type DebugLogLevel,
  type DebugEntry,
} from './debug-store';

const LEVELS: DebugLogLevel[] = ['debug', 'info', 'warn', 'error'];

const LEVEL_COLORS: Record<DebugLogLevel, string> = {
  error: '#ed4245',
  warn: '#fee75c',
  info: '#f2f3f5',
  debug: '#8a8a8a',
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${h}:${m}:${s}.${ms}`;
}

export function DebugConsole(): JSX.Element {
  const entries = useDebugStore((s) => s.entries);
  const categories = useDebugStore((s) => s.categories);
  const severity = useDebugStore((s) => s.severity);
  const toggle = useDebugStore((s) => s.toggle);
  const setCategory = useDebugStore((s) => s.setCategory);
  const setSeverity = useDebugStore((s) => s.setSeverity);

  const logRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  const filtered = entries.filter(
    (e: DebugEntry) =>
      categories[e.category] && LEVEL_RANK[e.level] >= LEVEL_RANK[severity],
  );

  useEffect(() => {
    if (autoScrollRef.current && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [filtered.length]);

  const handleScroll = useCallback(() => {
    const el = logRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    autoScrollRef.current = atBottom;
  }, []);

  const toggleCat = useCallback(
    (cat: DebugCategory) => {
      setCategory(cat, !categories[cat]);
    },
    [categories, setCategory],
  );

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '30vh',
        background: 'rgba(0, 0, 0, 0.85)',
        color: '#f2f3f5',
        fontFamily: 'monospace',
        fontSize: '12px',
        zIndex: 9000,
        display: 'flex',
        flexDirection: 'column',
        borderTop: '1px solid #444',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 8px',
          borderBottom: '1px solid #333',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Debug Console</span>
        {ALL_CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => toggleCat(cat)}
            style={{
              background: 'none',
              border: 'none',
              color: categories[cat] ? '#f2f3f5' : '#555',
              fontWeight: categories[cat] ? 'bold' : 'normal',
              fontFamily: 'monospace',
              fontSize: '11px',
              cursor: 'pointer',
              padding: '1px 4px',
            }}
          >
            {cat}
          </button>
        ))}
        <span style={{ marginLeft: 'auto' }} />
        <button
          onClick={toggle}
          style={{
            background: 'none',
            border: '1px solid #666',
            color: '#f2f3f5',
            cursor: 'pointer',
            padding: '1px 6px',
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        >
          ×
        </button>
      </div>

      {/* Severity bar */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          padding: '2px 8px',
          borderBottom: '1px solid #222',
          flexShrink: 0,
        }}
      >
        <span style={{ color: '#888', marginRight: '4px' }}>Severity:</span>
        {LEVELS.map((lvl) => (
          <button
            key={lvl}
            onClick={() => setSeverity(lvl)}
            style={{
              background: severity === lvl ? '#333' : 'none',
              border: severity === lvl ? '1px solid #666' : '1px solid transparent',
              color: LEVEL_COLORS[lvl],
              fontFamily: 'monospace',
              fontSize: '11px',
              cursor: 'pointer',
              padding: '1px 6px',
            }}
          >
            {lvl}
          </button>
        ))}
      </div>

      {/* Log entries */}
      <div
        ref={logRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '4px 8px',
        }}
      >
        {filtered.map((e: DebugEntry) => (
          <div key={e.id} style={{ color: LEVEL_COLORS[e.level], whiteSpace: 'pre-wrap' }}>
            [{formatTime(e.timestamp)}] [{e.category}] {e.message}
          </div>
        ))}
      </div>
    </div>
  );
}
